if (!customElements.get('fhs-product-zoom')) {
  customElements.define('fhs-product-zoom', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.abort = new AbortController();
      this.sources = new Map(Array.from(this.querySelectorAll('[data-source]'), el => [el.dataset.mediaId, el.dataset.source]));
      this.desktop = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.cache = new Map();
      this.panel = document.createElement('div');
      this.panel.className = 'fhs-zoom-panel';
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.hidden = true;
      this.detail = new Image();
      this.detail.alt = '';
      this.detail.draggable = false;
      this.panel.append(this.detail);
      document.body.append(this.panel);
      this.help = document.createElement('span');
      this.help.id = this.root.id + '-zoom-help';
      this.help.className = 'visually-hidden';
      this.help.textContent = this.dataset.instructions;
      this.root.append(this.help);
      const listen = (el, event, handler, options = {}) =>
        el.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      listen(this.root, 'pointerover', e => this.enter(e));
      listen(this.root, 'pointermove', e => {
        if (!this.active || e.pointerType !== 'mouse') return;
        this.point = { x: e.clientX, y: e.clientY };
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.paint();
        });
      });
      listen(this.root, 'pointerout', e => {
        if (this.active?.contains(e.target) && !this.active.contains(e.relatedTarget)) this.scheduleHide();
      });
      listen(this.panel, 'pointerenter', () => clearTimeout(this.hideTimer));
      listen(this.panel, 'pointerleave', () => this.hide());
      listen(this.root, 'click', e => this.openImage(e), { capture: true });
      listen(document, 'keydown', e => { if (e.key === 'Escape' || e.key === 'Tab') this.hide(); });
      listen(window, 'scroll', () => this.hide(), { capture: true, passive: true });
      listen(window, 'resize', () => this.hide(), { passive: true });
      listen(window, 'blur', () => this.hide());
      listen(this.desktop, 'change', () => {
        this.hide();
        this.root.querySelectorAll('.fhs-zoom-cue').forEach(cue => {
          cue.textContent = this.desktop.matches ? this.dataset.hoverLabel : this.dataset.touchLabel;
        });
      });
      this.observer = new MutationObserver(records => {
        if (this.active && (!this.active.isConnected || !this.active.getClientRects().length)) this.hide();
        if (records.some(r => r.type === 'childList')) this.decorate();
      });
      this.observer.observe(this.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.decorate();
    }
    decorate() {
      this.root.querySelectorAll('media-gallery .product__modal-opener--image').forEach(opener => {
        if (opener.classList.contains('fhs-zoom-ready')) return;
        const button = opener.querySelector('button[data-media-id]');
        if (!button || !this.sources.has(button.dataset.mediaId)) return;
        opener.classList.add('fhs-zoom-ready');
        const cue = document.createElement('span');
        cue.className = 'fhs-zoom-cue';
        cue.setAttribute('aria-hidden', 'true');
        cue.textContent = this.desktop.matches ? this.dataset.hoverLabel : this.dataset.touchLabel;
        opener.append(cue);
        button.setAttribute('aria-describedby', [button.getAttribute('aria-describedby'), this.help.id].filter(Boolean).join(' '));
      });
    }
    openImage(event) {
      const button = event.target.closest('.product__media-toggle[data-media-id]');
      if (!button || !button.closest('media-gallery')) return;
      this.hide();
      const source = this.sources.get(button.dataset.mediaId);
      const opener = button.closest('modal-opener');
      const modal = opener && document.querySelector(opener.dataset.modal);
      const image = modal && Array.from(modal.querySelectorAll('img[data-media-id]')).find(img => img.dataset.mediaId === button.dataset.mediaId);
      if (source && image) {
        image.removeAttribute('srcset');
        image.src = source;
      }
    }
    enter(event) {
      const opener = event.target.closest('media-gallery .fhs-zoom-ready');
      if (!opener || !this.desktop.matches || event.pointerType !== 'mouse') return;
      clearTimeout(this.hideTimer);
      if (opener === this.active) return;
      this.hide();
      const image = opener.querySelector('.product__media img');
      const source = this.sources.get(opener.querySelector('button[data-media-id]').dataset.mediaId);
      if (!image || !source) return;
      this.active = opener;
      this.image = image;
      this.point = { x: event.clientX, y: event.clientY };
      this.source = source;
      if (!this.cache.has(source)) {
        const loaded = new Image();
        loaded.decoding = 'async';
        const pending = new Promise(resolve => {
          loaded.onload = () => resolve(loaded);
          loaded.onerror = () => resolve(null);
        });
        this.cache.set(source, pending);
        loaded.src = source;
      }
      this.cache.get(source).then(loaded => {
        if (this.active !== opener || this.source !== source) return;
        if (!loaded) { this.cache.delete(source); this.hide(); return; }
        this.loaded = loaded;
        this.detail.src = source;
        this.paint();
      });
    }
    paint() {
      if (!this.active || !this.loaded || !this.desktop.matches) return;
      const box = this.image.getBoundingClientRect();
      const wrapper = this.active.closest('.product__media-wrapper').getBoundingClientRect();
      if (!box.width || !box.height || !this.image.naturalWidth || !this.image.naturalHeight) { this.hide(); return; }
      const rightSpace = innerWidth - wrapper.right - 28;
      const leftSpace = wrapper.left - 28;
      const onRight = rightSpace >= leftSpace;
      const width = Math.min(640, onRight ? rightSpace : leftSpace);
      const height = Math.min(600, innerHeight - 32);
      if (width < 260 || height < 220) { this.hide(); return; }
      const fit = getComputedStyle(this.image).objectFit;
      const ratio = this.image.naturalWidth / this.image.naturalHeight;
      let w = box.width, h = box.height;
      if (fit === 'contain' || fit === 'cover') {
        const scale = (fit === 'cover' ? Math.max : Math.min)(box.width / this.image.naturalWidth, box.height / this.image.naturalHeight);
        w = this.image.naturalWidth * scale;
        h = w / ratio;
      }
      const left = box.left + (box.width - w) / 2;
      const top = box.top + (box.height - h) / 2;
      const x = Math.max(0, Math.min(1, (this.point.x - left) / w));
      const y = Math.max(0, Math.min(1, (this.point.y - top) / h));
      const zoom = Math.max(2.5, width / w, height / h);
      const zw = w * zoom, zh = h * zoom;
      Object.assign(this.panel.style, {
        width: width + 'px', height: height + 'px',
        left: (onRight ? wrapper.right + 12 : wrapper.left - width - 12) + 'px',
        top: Math.max(16, Math.min(box.top, innerHeight - height - 16)) + 'px'
      });
      Object.assign(this.detail.style, {
        width: zw + 'px', height: zh + 'px',
        left: -Math.max(0, Math.min(zw - width, x * zw - width / 2)) + 'px',
        top: -Math.max(0, Math.min(zh - height, y * zh - height / 2)) + 'px'
      });
      this.panel.hidden = false;
      this.active.classList.add('fhs-zoom-active');
    }
    scheduleHide() {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => this.hide(), 180);
    }
    hide() {
      clearTimeout(this.hideTimer);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.active?.classList.remove('fhs-zoom-active');
      this.active = null;
      this.loaded = null;
      if (this.panel) this.panel.hidden = true;
    }
    disconnectedCallback() {
      this.hide();
      this.abort?.abort();
      this.observer?.disconnect();
      this.panel?.remove();
      this.root?.querySelectorAll('.fhs-zoom-ready').forEach(opener => {
        opener.classList.remove('fhs-zoom-ready');
        opener.querySelector('.fhs-zoom-cue')?.remove();
        const button = opener.querySelector('button[data-media-id]');
        const ids = button?.getAttribute('aria-describedby')?.split(' ').filter(id => id !== this.help?.id);
        if (ids?.length) button.setAttribute('aria-describedby', ids.join(' '));
        else button?.removeAttribute('aria-describedby');
      });
      this.help?.remove();
    }
  });
}
